<script setup>
const config = useRuntimeConfig()
const img = useImage()
const user = useStrapiUser()
const props = defineProps({
  card: {
    type: Object,
    default: () => {}
  },
  id: {
    type: Number,
    default: () => 0
  },
  ind: {
    type: Number,
    default: () => 0
  }
})

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  // Use UTC methods to avoid timezone differences between server and client
  const day = String(d.getUTCDate()).padStart(2, '0')
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const year = d.getUTCFullYear()
  return `${day}.${month}.${year}`
}

const isRU = computed(() => new Date(props.card.date).getTime() < 1644192000001)

const formattedDate = computed(() => formatDate(props.card.date))

const onlineCounts = useState('onlineAnimeCounts', () => ({}))
const onlineCount = computed(() => onlineCounts.value[props.card.url] || 0)
</script>

<template>
  <NuxtLink
    :to="`/watch/${card.url}`"
    class="group/card block isolate relative w-full"
  >
    <div
      class="relative shadow-md hover:shadow-2xl rounded-[12px] w-full overflow-hidden transition-shadow duration-500 mobile-aspect"
    >
      <!-- Poster with scale -->
      <NuxtImg
        provider="ipx"
        :src="`${useStrapiMedia(card?.poster.url)}`"
        format="webp"
        :alt="card.title"
        class="absolute inset-0 bg-base-100 w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-500 ease-out"
        :class="`card-${card.url}`"
        quality="80"
        :width="card?.poster.width"
        :height="card?.poster.height"
        sizes="180px lg:241px"
        :densities="'1x'"
        placeholder
        fetchpriority="high"
      />

      <!-- Hover gradient overlay -->
      <div
        class="z-10 absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
      />
      <div
        class="z-10 absolute inset-0 bg-gradient-to-b from-black/60 via-black/5 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
      />

      <!-- Play button -->
      <div
        class="z-10 absolute inset-0 flex justify-center items-center opacity-0 group-hover/card:opacity-100 scale-75 group-hover/card:scale-100 transition-all duration-300"
      >
        <div
          class="flex justify-center items-center bg-primary shadow-lg rounded-full w-12 h-12 text-primary-content"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <!-- Icon from MingCute Icon by MingCute Design - https://github.com/Richard9394/MingCute/blob/main/LICENSE -->
            <g
              fill="none"
              fill-rule="evenodd"
            >
              <path
                d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"
              />
              <path
                fill="currentColor"
                d="M5.669 4.76a1.47 1.47 0 0 1 2.04-1.177c1.062.454 3.442 1.533 6.462 3.276c3.021 1.744 5.146 3.267 6.069 3.958c.788.591.79 1.763.001 2.356c-.914.687-3.013 2.19-6.07 3.956c-3.06 1.766-5.412 2.832-6.464 3.28c-.906.387-1.92-.2-2.038-1.177c-.138-1.142-.396-3.735-.396-7.237c0-3.5.257-6.092.396-7.235"
              />
            </g>
          </svg>
        </div>
      </div>

      <!-- Private overlay -->
      <div
        v-if="card.privat && !user?.privat"
        class="z-10 absolute inset-0 backdrop-blur-sm"
      >
        <div
          class="top-1/2 left-1/2 absolute flex flex-col justify-center items-center bg-neutral bg-opacity-80 p-[20px] rounded-md w-[90%] font-bold text-neutral-content text-lg text-center -translate-x-1/2 -translate-y-1/2"
        >
          <svg
            class="w-[50%] h-[50%]"
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M18 18q.625 0 1.063-.437T19.5 16.5t-.437-1.062T18 15t-1.062.438T16.5 16.5t.438 1.063T18 18m0 3q.75 0 1.4-.35t1.075-.975q-.575-.35-1.2-.513T18 19t-1.275.162t-1.2.513q.425.625 1.075.975T18 21M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6zm3.25 14H6q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v1.3q-.45-.15-.937-.225T18 11v-1H6v10h5.3q.2.6.4 1.038t.55.962M18 23q-2.075 0-3.537-1.463T13 18t1.463-3.537T18 13t3.538 1.463T23 18t-1.463 3.538T18 23M6 10v10z"
            />
          </svg>
          <div class="leading-[110%]">Для перегляду потрібна підписка</div>
        </div>
      </div>

      <!-- Top right badge: RU/UA -->
      <div
        class="top-[10px] right-[10px] z-20 absolute flex items-center gap-[5px] bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content lg:text-md text-sm stars"
      >
        {{ isRU ? 'RU' : 'UA' }}
      </div>

      <!-- Top left badge: date -->
      <div
        class="top-[10px] left-[10px] z-20 absolute flex items-center gap-[5px]"
      >
        <div
          class="flex items-center gap-[5px] bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content lg:text-md text-sm stars"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            aria-hidden="true"
            role="img"
            class="iconify iconify--material-symbols"
            width="18"
            height="18"
            preserveAspectRatio="xMidYMid meet"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M8 14q-.425 0-.713-.288Q7 13.425 7 13t.287-.713Q7.575 12 8 12t.713.287Q9 12.575 9 13t-.287.712Q8.425 14 8 14Zm4 0q-.425 0-.712-.288Q11 13.425 11 13t.288-.713Q11.575 12 12 12t.713.287Q13 12.575 13 13t-.287.712Q12.425 14 12 14Zm4 0q-.425 0-.712-.288Q15 13.425 15 13t.288-.713Q15.575 12 16 12t.712.287Q17 12.575 17 13t-.288.712Q16.425 14 16 14ZM5 22q-.825 0-1.413-.587Q3 20.825 3 20V6q0-.825.587-1.412Q4.175 4 5 4h1V2h2v2h8V2h2v2h1q.825 0 1.413.588Q21 5.175 21 6v14q0 .825-.587 1.413Q19.825 22 19 22Zm0-2h14V10H5v10Z"
            ></path>
          </svg>
          {{ formattedDate }}
        </div>

        <div
          v-if="onlineCount > 0"
          class="top-[28px] lg:top-[unset] absolute lg:relative flex items-center gap-[6px] bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content lg:text-md text-sm stars"
          title="Зараз дивляться"
        >
          <span class="relative flex w-[8px] h-[8px]">
            <span
              class="inline-flex absolute bg-success opacity-75 rounded-full w-full h-full"
            ></span>
            <span
              class="inline-flex relative bg-success rounded-full w-[8px] h-[8px]"
            ></span>
          </span>
          {{ onlineCount }}
        </div>
      </div>

      <!-- Bottom right: stars / watching / tooltip -->
      <div
        v-if="card.stars > 0 && !card.tooltip"
        class="right-[10px] bottom-[10px] z-20 absolute flex items-center bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content lg:text-lg stars"
      >
        {{ card.stars }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          aria-hidden="true"
          role="img"
          class="iconify iconify--ic"
          width="24"
          height="24"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="m12 17.27l4.15 2.51c.76.46 1.69-.22 1.49-1.08l-1.1-4.72l3.67-3.18c.67-.58.31-1.68-.57-1.75l-4.83-.41l-1.89-4.46c-.34-.81-1.5-.81-1.84 0L9.19 8.63l-4.83.41c-.88.07-1.24 1.17-.57 1.75l3.67 3.18l-1.1 4.72c-.2.86.73 1.54 1.49 1.08l4.15-2.5z"
          ></path>
        </svg>
      </div>
      <div
        v-if="!card.watched && !card.tooltip"
        class="right-[10px] bottom-[10px] z-20 absolute flex items-center bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content text-sm lg:text-lg stars"
      >
        {{ $t('watching') }}
      </div>
      <div
        v-if="card.tooltip"
        class="right-[10px] bottom-[10px] z-20 absolute flex items-center bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit max-w-[80%] font-medium text-[12px] text-neutral-content lg:text-[15px] stars"
        v-html="card.tooltip"
      />
    </div>

    <!-- Title & meta -->
    <div class="mt-[8px] px-[2px]">
      <h2
        class="block overflow-hidden font-bold group-hover/card:text-primary text-sm text-ellipsis leading-tight whitespace-nowrap transition-colors"
      >
        {{ card.title }}
      </h2>
      <h3
        class="block mt-[2px] overflow-hidden text-[12px] text-base-content/60 text-ellipsis whitespace-nowrap"
      >
        {{
          card.maxepisodes == 1
            ? $t('film')
            : card.nowepisode + ' / ' + card.maxepisodes
        }}
        • {{ card.genres.toString().split(',').join(', ') }}
      </h3>
    </div>
  </NuxtLink>
</template>

<style>
.showAnimation {
  animation: showCard 0.25s ease-in-out;
}

@keyframes showCard {
  from {
    opacity: 0;
    transform: scale(0.96);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.mobile-aspect {
  aspect-ratio: 2 / 3;
}
</style>
